import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, Lock, Mail } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';

import { adminLoginSchema, type AdminLoginValues } from '../auth.schemas';
import { resolveAuthErrorMessage } from '../auth.messages';
import { useLogin } from '../hooks/use-login';
import { PasswordInput } from './password-input';

interface AdminLoginFormProps {
  onAuthenticated: () => void;
}

/** Acceso de administradores: correo electrónico + contraseña. */
export function AdminLoginForm({ onAuthenticated }: AdminLoginFormProps) {
  const login = useLogin();

  const [serverError, setServerError] = useState<string | null>(null);
  const [showRecoveryHelp, setShowRecoveryHelp] = useState(false);
  const [remember, setRemember] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AdminLoginValues>({
    resolver: zodResolver(adminLoginSchema),
    defaultValues: { identity: '', password: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);

    try {
      await login.mutateAsync({ ...values, type: 'admin', remember });
      onAuthenticated();
    } catch (error) {
      setServerError(resolveAuthErrorMessage(error));
    }
  });

  const isBusy = login.isPending;

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-4">
      {serverError ? (
        <div className="animate-shake">
          <Alert variant="error">{serverError}</Alert>
        </div>
      ) : null}

      <Input
        label="Correo electrónico"
        type="email"
        inputMode="email"
        autoComplete="username"
        placeholder="usuario@empresa.com"
        leftIcon={<Mail className="size-4.5" />}
        error={errors.identity?.message}
        disabled={isBusy}
        autoFocus
        {...register('identity')}
      />

      <PasswordInput
        label="Contraseña"
        autoComplete="current-password"
        placeholder="Tu contraseña"
        leftIcon={<Lock className="size-4.5" />}
        error={errors.password?.message}
        disabled={isBusy}
        {...register('password')}
      />

      <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
        <Checkbox
          label="Mantener sesión"
          checked={remember}
          disabled={isBusy}
          onChange={(event) => setRemember(event.target.checked)}
        />

        <button
          type="button"
          onClick={() => setShowRecoveryHelp((current) => !current)}
          className="cursor-pointer text-sm font-medium text-brand-600 transition-colors hover:text-brand-700 hover:underline"
        >
          ¿Olvidaste tu contraseña?
        </button>
      </div>

      {showRecoveryHelp ? (
        <Alert variant="info">
          El restablecimiento de contraseña se realiza desde el módulo de <strong>Usuarios</strong>.
          Solicítalo al administrador del sistema o a soporte de Escienza.
        </Alert>
      ) : null}

      <Button
        type="submit"
        size="lg"
        fullWidth
        loading={isBusy}
        className="mt-1"
        rightIcon={<ArrowRight className="size-4" />}
      >
        Iniciar sesión
      </Button>
    </form>
  );
}
